import { z } from 'zod';

/**
 * The ONE spelling of the box enroll one-liner. The dashboard (`joinOneLiner`,
 * apps/dashboard `src/lib/fleet.ts`) and MCP (`boxJoinCommand`,
 * packages/mcp `src/army/box.ts`) both render it from here, so an agent following
 * the MCP answer and an operator copying the dashboard line run the same command.
 * `joinUrl` is the absolute `GET /join` URL (`<apiBase>/join`, trailing-slash
 * normalized by the caller).
 *
 *  - With no `model`, it asks for the CODING MODEL first and EXPORTS it as
 *    `DZ_MODEL_OVERRIDE`, because the join script's model gate (api
 *    `routes/join.ts`) refuses a fresh box that names none, before it installs
 *    anything and before it spends the token. A blank answer keeps a
 *    `DZ_MODEL_OVERRIDE` the shell already exported, and otherwise passes blank so
 *    the script stays the arbiter (a re-joined box keeps the model it already
 *    names). Nothing is pre-filled by GUESSING: a guessed model is the defect
 *    that gate exists to refuse.
 *  - `{ model }` is a model a PERSON typed (the dashboard's Connect a machine
 *    modal, owner ruling G15) or an MCP caller named (`box add`), which is not a
 *    guess. When it matches {@link BOX_MODEL_ID_PATTERN} the command exports it as
 *    `DZ_MODEL_OVERRIDE=<model>` instead of prompting. The gate is HERE, in the
 *    one renderer, rather than at each caller: the value lands unquoted in a
 *    shell `export`, and the pattern's charset (letters, digits, `. _ - : /`) is
 *    exactly what makes that shell-safe. A model outside it leaves the command
 *    byte-for-byte the prompt-first form, never a forged line. The join gate
 *    still validates whatever arrives.
 *  - The token is READ INTERACTIVELY (`read -rsp`) and rides the
 *    `X-Dz-Enroll-Token` HEADER, the only form `GET /join` accepts: a query token
 *    leaks a live credential into access and proxy logs and referrers, and a
 *    literal lands in the operator's shell history.
 *  - The script is downloaded to a temp file and THEN run, never `curl … | bash`:
 *    a piped `bash` exits 0 on empty stdin, so a failed `curl -f` download would
 *    read as a successful enroll. `-o` makes the download's failure the command's
 *    failure and `&&` gates `bash`.
 */
export function renderBoxJoinCommand(
  joinUrl: string,
  options: { readonly model?: string | undefined } = {},
): string {
  const model = options.model;
  const head =
    model !== undefined && BOX_MODEL_ID_PATTERN.test(model)
      ? `export DZ_MODEL_OVERRIDE=${model}`
      : `read -rp 'Coding model (e.g. z-ai/glm-5.3): ' DZ_MODEL && export DZ_MODEL_OVERRIDE="\${DZ_MODEL:-\${DZ_MODEL_OVERRIDE:-}}"`;
  return `${head} && read -rsp 'Enrollment token: ' DZ_ENROLL_TOKEN && DZ_JOIN="$(mktemp)" && curl -sfL -H "X-Dz-Enroll-Token: $DZ_ENROLL_TOKEN" ${joinUrl} -o "$DZ_JOIN" && bash "$DZ_JOIN"`;
}

/**
 * The model-id charset the join script's model gate admits
 * (`*[!A-Za-z0-9._:/-]*` refuses, api `routes/join.ts`). The value lands in a
 * `KEY=value` env file that systemd and docker each parse, so a newline or quote
 * would forge a line. Every id driven so far fits it, bare (`claude-opus-5`) or
 * slashed (`z-ai/glm-5.3`).
 */
export const BOX_MODEL_ID_PATTERN = /^[A-Za-z0-9._:/-]+$/;

/**
 * A box's coding model as a door accepts it before handing it to the join as
 * `DZ_MODEL_OVERRIDE` — the SSH-pull enroll body (REST) and `box enroll_ssh`
 * (MCP) parse the same value, so neither can admit what the gate refuses.
 */
export const BoxModelIdSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(BOX_MODEL_ID_PATTERN, 'not a model id (allowed: letters, digits and . _ - : /)');
